
import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useMaintenanceMode } from "@/hooks/useMaintenanceMode";
import MaintenanceScreen from "@/components/MaintenanceScreen";
import Layout from "./components/Layout";
import { AuthProvider } from "./hooks/useAuth";
import AdminLayout from "./components/AdminLayout";
import ProtectedRoute from "./components/ProtectedRoute";

// Critical pages - load immediately
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";

// Lazy load all other pages to reduce initial bundle size
const About = lazy(() => import("./pages/About"));
const Training = lazy(() => import("./pages/Training"));
const TrainingBeginners = lazy(() => import("./pages/TrainingBeginners"));
const Tournaments = lazy(() => import("./pages/Tournaments"));
const TournamentArchive = lazy(() => import("./pages/TournamentArchive"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Events = lazy(() => import("./pages/Events"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Contact = lazy(() => import("./pages/Contact"));
const Auth = lazy(() => import("./pages/Auth"));
const Impressum = lazy(() => import("./pages/Impressum"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const AGB = lazy(() => import("./pages/AGB"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Search = lazy(() => import("./pages/Search"));
const Downloads = lazy(() => import("./pages/Downloads"));

// Admin pages - lazy load for better performance
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));
const BlogManagement = lazy(() => import("./pages/admin/BlogManagement"));
const MediaManagement = lazy(() => import("./pages/admin/MediaManagement"));
const EventManagement = lazy(() => import("./pages/admin/EventManagement"));
const TournamentManagement = lazy(() => import("./pages/admin/TournamentManagement"));
const ClubRankingsManagement = lazy(() => import("./pages/admin/ClubRankingsManagement"));
const FileManagement = lazy(() => import("./pages/admin/FileManagement"));
const BannerManagement = lazy(() => import("./pages/admin/BannerManagement"));
const Settings = lazy(() => import("./pages/admin/Settings"));
const TrafficAnalytics = lazy(() => import("./pages/admin/TrafficAnalytics"));
const TrainingManagement = lazy(() => import("./pages/admin/TrainingManagement"));
const TrainingRequests = lazy(() => import("./pages/admin/TrainingRequests"));
const BlogTrash = lazy(() => import("./pages/admin/BlogTrash"));
const AboutManagement = lazy(() => import("./pages/admin/AboutManagement"));
const ContactManagement = lazy(() => import("./pages/admin/ContactManagement"));

// Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
  </div>
);


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 60 * 1000, // 30 minutes - longer for static content
      gcTime: 60 * 60 * 1000, // 60 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: 2,
      // Stale-while-revalidate pattern
      refetchInterval: false,
      // Keep previous data while fetching
      placeholderData: (previousData) => previousData,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <MaintenanceWrapper />
    </AuthProvider>
  </QueryClientProvider>
);

function MaintenanceWrapper() {
  const { isMaintenanceMode, loading } = useMaintenanceMode();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isMaintenanceMode) {
    return <MaintenanceScreen />;
  }

  return (
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Layout><Home /></Layout>} />
            <Route path="/about" element={<Layout title="Über uns – ASC Fechten Wien" description="Lernen Sie den Akademischen Sportclub Fechten Wien kennen: Vereinsgeschichte seit 1970, Vorstand, Trainerinnen und Trainer sowie unsere Werte im Fechtsport."><Suspense fallback={<PageLoader />}><About /></Suspense></Layout>} />
            <Route path="/training" element={<Layout title="Fechttraining in Wien – Zeiten & Gruppen" description="Trainingszeiten, Gruppen und Preise beim ASC Fechten Wien: Fechttraining für Kinder, Jugendliche und Erwachsene – Anfänger bis Wettkampfsport. Probetraining möglich."><Suspense fallback={<PageLoader />}><Training /></Suspense></Layout>} />
            <Route path="/training/anfaenger" element={<Layout><Suspense fallback={<PageLoader />}><TrainingBeginners /></Suspense></Layout>} />
            <Route path="/tournaments" element={<Layout title="Turniere – ASC Fechten Wien" description="Aktuelle und vergangene Fechtturniere des ASC Fechten Wien: Ausschreibungen, Anmeldung, Ergebnisse und Clubranglisten der laufenden Saison."><Suspense fallback={<PageLoader />}><Tournaments /></Suspense></Layout>} />
            <Route path="/tournaments/archive" element={<Layout title="Turnierarchiv – ASC Fechten Wien" description="Das Turnierarchiv des ASC Fechten Wien: alle vergangenen Turniere nach Jahr und Waffe gefiltert, inklusive Ergebnisse und Platzierungen."><Suspense fallback={<PageLoader />}><TournamentArchive /></Suspense></Layout>} />
            <Route path="/events" element={<Layout title="Termine & Veranstaltungen – ASC Fechten Wien" description="Alle Termine des ASC Fechten Wien auf einen Blick: Turniere, Vereinsveranstaltungen und Trainingslager mit Datum, Uhrzeit und Ort."><Suspense fallback={<PageLoader />}><Events /></Suspense></Layout>} />
            <Route path="/gallery" element={<Layout title="Galerie – Bilder vom ASC Fechten Wien" description="Fotogalerie des ASC Fechten Wien: Eindrücke von Turnieren, Trainings und Vereinsveranstaltungen, filterbar nach Kategorie, Event und Jahr."><Suspense fallback={<PageLoader />}><Gallery /></Suspense></Layout>} />
            <Route path="/search" element={<Layout title="Suche – ASC Fechten Wien" description="Durchsuchen Sie die Inhalte des ASC Fechten Wien: Beiträge, Turniere, Termine und Informationen rund um den Fechtsport in Wien." noindex><Suspense fallback={<PageLoader />}><Search /></Suspense></Layout>} />
            <Route path="/downloads" element={<Layout title="Downloads & Dokumente – ASC Fechten Wien" description="Dokumente des ASC Fechten Wien zum Download: Ausschreibungen, Formulare, Statuten und Informationsblätter rund um Training und Turniere."><Suspense fallback={<PageLoader />}><Downloads /></Suspense></Layout>} />
            <Route path="/blog" element={<Layout title="News & Berichte – ASC Fechten Wien" description="Aktuelle News, Turnierberichte und Vereinsnachrichten des ASC Fechten Wien – alle Beiträge rund um unseren Fechtclub in Wien."><Suspense fallback={<PageLoader />}><Blog /></Suspense></Layout>} />
            <Route path="/blog/:slug" element={<Layout><Suspense fallback={<PageLoader />}><BlogPost /></Suspense></Layout>} />
            <Route path="/contact" element={<Layout title="Kontakt – ASC Fechten Wien" description="Kontaktieren Sie den ASC Fechten Wien: Ansprechpersonen, Telefonnummern, E-Mail-Adressen sowie Anfahrt zur Fechthalle in Wien."><Suspense fallback={<PageLoader />}><Contact /></Suspense></Layout>} />
            <Route path="/auth" element={<Layout title="Anmelden – ASC Fechten Wien" description="Interner Anmeldebereich für Vereinsmitglieder und Administratoren des ASC Fechten Wien." noindex><Suspense fallback={<PageLoader />}><Auth /></Suspense></Layout>} />
            <Route path="/impressum" element={<Layout title="Impressum – ASC Fechten Wien" description="Impressum und Offenlegung gemäß Mediengesetz für die Website des Akademischen Sportclubs Fechten Wien."><Suspense fallback={<PageLoader />}><Impressum /></Suspense></Layout>} />
            <Route path="/datenschutz" element={<Layout title="Datenschutz – ASC Fechten Wien" description="Datenschutzerklärung des ASC Fechten Wien: Welche Daten wir verarbeiten, zu welchem Zweck und welche Rechte Sie als Besucher haben."><Suspense fallback={<PageLoader />}><Datenschutz /></Suspense></Layout>} />
            <Route path="/agb" element={<Layout title="AGB – ASC Fechten Wien" description="Allgemeine Geschäftsbedingungen des ASC Fechten Wien für Mitgliedschaft, Trainingsteilnahme und Turnieranmeldungen."><Suspense fallback={<PageLoader />}><AGB /></Suspense></Layout>} />
            <Route path="/unsubscribe" element={<Suspense fallback={<PageLoader />}><Unsubscribe /></Suspense>} />
            
            {/* Admin Routes - All lazy loaded */}
            <Route path="/admin" element={
              <ProtectedRoute requiredRole={["admin", "moderator"]}>
                <AdminLayout><Suspense fallback={<PageLoader />}><Dashboard /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute requiredRole="admin">
                <AdminLayout><Suspense fallback={<PageLoader />}><UserManagement /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/training" element={
              <ProtectedRoute requiredRole={["admin", "moderator"]}>
                <AdminLayout><Suspense fallback={<PageLoader />}><TrainingManagement /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/training-requests" element={
              <ProtectedRoute requiredRole={["admin", "moderator"]}>
                <AdminLayout><Suspense fallback={<PageLoader />}><TrainingRequests /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/blog" element={
              <ProtectedRoute requiredRole={["admin", "moderator"]}>
                <AdminLayout><Suspense fallback={<PageLoader />}><BlogManagement /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/blog/trash" element={
              <ProtectedRoute requiredRole={["admin", "moderator"]}>
                <AdminLayout><Suspense fallback={<PageLoader />}><BlogTrash /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/media" element={
              <ProtectedRoute requiredRole={["admin", "moderator"]}>
                <AdminLayout><Suspense fallback={<PageLoader />}><MediaManagement /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/events" element={
              <ProtectedRoute requiredRole={["admin", "moderator"]}>
                <AdminLayout><Suspense fallback={<PageLoader />}><EventManagement /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/tournaments" element={
              <ProtectedRoute requiredRole={["admin", "moderator"]}>
                <AdminLayout><Suspense fallback={<PageLoader />}><TournamentManagement /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/club-rankings" element={
              <ProtectedRoute requiredRole={["admin", "moderator"]}>
                <AdminLayout><Suspense fallback={<PageLoader />}><ClubRankingsManagement /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/files" element={
              <ProtectedRoute requiredRole={["admin", "moderator"]}>
                <AdminLayout><Suspense fallback={<PageLoader />}><FileManagement /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/banner" element={
              <ProtectedRoute requiredRole="admin">
                <AdminLayout><Suspense fallback={<PageLoader />}><BannerManagement /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/settings" element={
              <ProtectedRoute requiredRole="admin">
                <AdminLayout><Suspense fallback={<PageLoader />}><Settings /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/analytics" element={
              <ProtectedRoute requiredRole={["admin", "moderator"]}>
                <AdminLayout><Suspense fallback={<PageLoader />}><TrafficAnalytics /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/about" element={
              <ProtectedRoute requiredRole={["admin", "moderator"]}>
                <AdminLayout><Suspense fallback={<PageLoader />}><AboutManagement /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/contact" element={
              <ProtectedRoute requiredRole={["admin", "moderator"]}>
                <AdminLayout><Suspense fallback={<PageLoader />}><ContactManagement /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="*" element={<Layout><NotFound /></Layout>} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  );
}

export default App;
